/**
 * MSAL authentication provider wrapping the application.
 * Initializes the PublicClientApplication and provides it to child components.
 */

import { type ReactNode, useEffect, useState } from "react";
import {
  PublicClientApplication,
  EventType,
  type AuthenticationResult,
  type EventMessage,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "@/lib/msal-config";

const msalInstance = new PublicClientApplication(msalConfig);

interface AuthProviderProps {
  readonly children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      await msalInstance.initialize();

      // Handle redirect response after login
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        msalInstance.setActiveAccount(response.account);
      }

      // Set active account if none is set
      const accounts = msalInstance.getAllAccounts();
      if (!msalInstance.getActiveAccount() && accounts.length > 0 && accounts[0]) {
        msalInstance.setActiveAccount(accounts[0]);
      }

      // Listen for login events to set active account
      msalInstance.addEventCallback((event: EventMessage) => {
        if (
          event.eventType === EventType.LOGIN_SUCCESS &&
          event.payload
        ) {
          const result = event.payload as AuthenticationResult;
          msalInstance.setActiveAccount(result.account);
        }
      });

      setIsInitialized(true);
    };

    initialize().catch((err: unknown) => {
      const message = err instanceof Error ? err.message : "Unknown MSAL initialization error";
      console.error(`[Auth] Failed to initialize MSAL: ${message}`);
    });
  }, []);

  if (!isInitialized) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-sc-bg-page">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-4 border-sc-primary border-t-transparent" />
          <p className="text-body-sm text-sc-text-muted">Initializing authentication...</p>
        </div>
      </div>
    );
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

/**
 * Exported for use in the API client and other non-component contexts.
 */
export { msalInstance };
